// ReactionsStrip — six-emoji bottom strip rendered inside CookMode
// for any active cook session with participants. Tapping an emoji
// inserts a row into cook_session_reactions; the strip listens to
// realtime broadcasts on the same table so every participant sees
// the burst pop on their screen for ~3.5 seconds before fading.
//
// Ephemeral by design: rows accumulate in the DB but the UI only
// renders entries received in the last 6 seconds. A pg_cron sweep
// can prune the table later — the read pattern is realtime-only,
// so old rows have zero UI cost.

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { COLOR, FONT } from "../lib/tokens";
import { supabase } from "../lib/supabase";

const REACTIONS = ["🎉", "👍", "😍", "🤔", "🍴", "❤️"];
const BURST_TTL_MS = 3500;

export default function ReactionsStrip({ cookSessionId, userId, isLead = false }) {
  const [bursts, setBursts] = useState([]); // [{ id, emoji, atY, atX, fromUser }]
  const channelRef = useRef(null);

  // Realtime listener for incoming reactions
  useEffect(() => {
    if (!cookSessionId) return undefined;
    const ch = supabase
      .channel(`rt:cook-reactions:${cookSessionId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "cook_session_reactions",
          filter: `cook_session_id=eq.${cookSessionId}`,
        },
        (payload) => {
          const row = payload.new;
          if (!row?.reaction) return;
          const id = row.id || `${row.created_at}-${row.user_id}-${row.reaction}`;
          const fromMe = row.user_id === userId;
          // Random horizontal jitter so multiple bursts don't stack
          const atX = 30 + Math.random() * 40; // 30–70% from left
          setBursts((prev) => [...prev, { id, emoji: row.reaction, atX, fromMe }]);
          setTimeout(() => {
            setBursts((prev) => prev.filter((b) => b.id !== id));
          }, BURST_TTL_MS);
        },
      )
      .subscribe();
    channelRef.current = ch;
    return () => {
      try { supabase.removeChannel(ch); } catch {}
      channelRef.current = null;
    };
  }, [cookSessionId, userId]);

  const send = (emoji) => {
    if (!cookSessionId || !userId) return;
    // Optimistic local burst — feels instant even if the INSERT
    // is slow. The realtime payload still arrives but we de-dup
    // by checking if a burst with the same temp id already exists.
    const tempId = `local-${Date.now()}-${Math.random()}`;
    setBursts((prev) => [...prev, { id: tempId, emoji, atX: 30 + Math.random() * 40, fromMe: true }]);
    setTimeout(() => {
      setBursts((prev) => prev.filter((b) => b.id !== tempId));
    }, BURST_TTL_MS);
    supabase.from("cook_session_reactions").insert({
      cook_session_id: cookSessionId,
      user_id: userId,
      reaction: emoji,
    }).then(({ error }) => {
      if (error) console.warn("[reactions] insert:", error.message);
    });
  };

  if (!cookSessionId) return null;

  // Portal to document.body so position:fixed escapes any ancestor
  // backdrop-filter / transform that would trap it. Also clears the
  // kid-mode bottom tab bar — bursts start well ABOVE it (180px +
  // safe area) so they rise INTO the visible content area, not
  // behind the nav.
  const overlay = (
    <>
      {/* Floating bursts — each emoji rises + fades */}
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          top: 0,
          pointerEvents: "none",
          zIndex: 9000,
          overflow: "hidden",
        }}
      >
        {bursts.map((b) => (
          <div
            key={b.id}
            style={{
              position: "absolute",
              left: `${b.atX}%`,
              // Start well above the kid-mode tab bar (~80px) +
              // safe area + the sender-strip pill (~52px). Total
              // ~180px from the bottom so bursts spawn IN the
              // visible content area, not behind the nav.
              bottom: "calc(180px + env(safe-area-inset-bottom))",
              fontSize: 48,
              animation: "reaction-rise 3.5s cubic-bezier(0.25, 0.5, 0.5, 1) forwards",
              filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.25))",
              pointerEvents: "none",
            }}
          >
            {b.emoji}
          </div>
        ))}
      </div>

      {/* Sender strip — ONLY for non-leads. The lead is busy cooking
          and doesn't need to react to their own work. They still see
          the incoming bursts from everyone else above. Anchored above
          the kid-mode tab bar (~80px high). */}
      {!isLead && (
      <div
        style={{
          position: "fixed",
          left: "50%",
          transform: "translateX(-50%)",
          bottom: "calc(96px + env(safe-area-inset-bottom))",
          display: "flex",
          gap: 6,
          padding: "8px 12px",
          background: "rgba(255,255,255,0.96)",
          border: `1px solid ${COLOR.border}`,
          borderRadius: 999,
          boxShadow: "0 6px 20px rgba(0,0,0,0.18)",
          zIndex: 9001,
          touchAction: "manipulation",
        }}
      >
        {REACTIONS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => send(emoji)}
            aria-label={`React with ${emoji}`}
            className="mcm-focusable"
            style={{
              all: "unset",
              cursor: "pointer",
              width: 36,
              height: 36,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
              borderRadius: "50%",
              transition: "transform 120ms ease, background 120ms ease",
              touchAction: "manipulation",
            }}
            onPointerDown={(e) => {
              e.currentTarget.style.transform = "scale(0.85)";
              e.currentTarget.style.background = COLOR.soil;
            }}
            onPointerUp={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.background = "transparent";
            }}
            onPointerLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.background = "transparent";
            }}
          >
            {emoji}
          </button>
        ))}
      </div>
      )}

      {/* Keyframe — appended once via a style tag in the same overlay */}
      <style>{`
        @keyframes reaction-rise {
          0%   { transform: translateY(0) scale(0.9); opacity: 0; }
          15%  { transform: translateY(-20px) scale(1.1); opacity: 1; }
          80%  { transform: translateY(-160px) scale(1); opacity: 1; }
          100% { transform: translateY(-220px) scale(0.85); opacity: 0; }
        }
      `}</style>
    </>
  );

  return createPortal(overlay, document.body);
}
